'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useActionState } from 'react';
import { useFormStatus } from 'react-dom';
import { createClient } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

type LoginState = { error?: string; redirectTo?: string } | null;

function LoginSubmitButton() {
  const { pending } = useFormStatus();
  return (
    <Button type="submit" className="w-full" disabled={pending} aria-busy={pending}>
      {pending ? 'Signing in…' : 'Sign in'}
    </Button>
  );
}

function LoginRedirectEffect({
  state,
  onRedirect,
}: {
  state: LoginState;
  onRedirect: (path: string) => void;
}) {
  const { pending } = useFormStatus();
  const prevPending = useRef(false);
  useEffect(() => {
    if (prevPending.current && !pending && state?.redirectTo) {
      onRedirect(state.redirectTo);
    }
    prevPending.current = pending;
  }, [pending, state, onRedirect]);
  return null;
}

export function LoginForm({ inviteOnly = false }: { inviteOnly?: boolean }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const errorParam = searchParams.get('error');
  const [urlError] = useState<string | null>(
    errorParam === 'auth_callback_error'
      ? 'Sign-in was cancelled or failed. Please try again.'
      : null
  );
  const [oauthLoading, setOauthLoading] = useState(false);

  const [state, formAction] = useActionState<LoginState, FormData>(
    async (_prev: LoginState, formData: FormData) => {
      const supabase = createClient();
      const emailEntry = formData.get('email');
      const passwordEntry = formData.get('password');
      const email = typeof emailEntry === 'string' ? emailEntry : '';
      const password = typeof passwordEntry === 'string' ? passwordEntry : '';
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error: error.message };
      return { redirectTo: searchParams.get('redirectTo') ?? '/dashboard' };
    },
    null
  );

  async function handleGoogleSignIn() {
    setOauthLoading(true);
    const redirectTo = searchParams.get('redirectTo') ?? '/dashboard';
    const callbackUrl =
      typeof window !== 'undefined'
        ? `${window.location.origin}/auth/callback?next=${encodeURIComponent(redirectTo)}`
        : '/auth/callback';

    const supabase = createClient();
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: callbackUrl },
    });

    if (oauthError) {
      setOauthLoading(false);
    }
    // else: browser redirects to Google
  }

  const displayError = urlError ?? state?.error ?? null;

  return (
    <div className="space-y-4">
      {!inviteOnly && (
        <>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            data-testid="google-oauth-button"
            aria-label="Sign in with Google"
            onClick={() => void handleGoogleSignIn()}
            disabled={oauthLoading}
          >
            <GoogleIcon className="h-5 w-5" />
            {oauthLoading ? 'Redirecting…' : 'Sign in with Google'}
          </Button>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-card px-2 text-muted-foreground">or continue with email</span>
            </div>
          </div>
        </>
      )}
      <form action={formAction} className="space-y-4">
        <LoginRedirectEffect
          state={state}
          onRedirect={(path) => {
            router.push(path);
            router.refresh();
          }}
        />
        {displayError && (
          <div
            className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
            role="alert"
            aria-live="assertive"
          >
            {displayError}
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" name="email" type="email" autoComplete="email" required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
          />
        </div>
        <LoginSubmitButton />
      </form>
    </div>
  );
}
